import { merge, set, get } from "@mongez/reinforcements";
import { plainConverter } from "./converters.js";
import { localizationEvents } from "./events.js";
/**
 * Current locale code
 */
let currentLocaleCode = "en";
/**
 * Current converter
 */
let currentConverter = plainConverter;
/**
 * all keywords for all locale codes
 */
let translationsList = {};
/**
 * Current fall back locale code
 */
let fallbackLocaleCode = "en";
/**
 * Get current locale code
 */
function getCurrentLocaleCode() {
  return currentLocaleCode;
}
/**
 * Get fallback locale code
 */
function getFallbackLocaleCode() {
  return fallbackLocaleCode;
}
/**
 * Set current converter
 */
function setConverter(converter) {
  currentConverter = converter;
}
/**
 * Set current locale code
 */
function setCurrentLocaleCode(localeCode) {
  const oldLocaleCode = currentLocaleCode;
  currentLocaleCode = localeCode;
  localizationEvents.triggerChange("localeCode", localeCode, oldLocaleCode);
}
/**
 * Add keywords
 */
function extend(localeCode, keywords) {
  translationsList[localeCode] = merge(
    translationsList[localeCode] || {},
    keywords,
  );
}
/**
 * Create a grouped translations based on keyword, each keyword contains list of locale codes and beside it its corresponding translation
 */
function groupedTranslations(groupKey, groupedTranslations) {
  if (typeof groupKey !== "string" && !groupedTranslations) {
    groupedTranslations = groupKey;
    groupKey = undefined;
  }
  for (const keyword in groupedTranslations) {
    const translations = groupedTranslations[keyword];
    for (const localeCode in translations) {
      if (!translationsList[localeCode]) {
        translationsList[localeCode] = {};
      }
      if (groupKey) {
        set(
          translationsList,
          `${localeCode}.${groupKey}.${keyword}`,
          translations[localeCode],
        );
      } else {
        translationsList[localeCode][keyword] = translations[localeCode];
      }
    }
  }
}
/**
 * Override the entire translations list
 */
function setTranslationsList(translations) {
  translationsList = translations;
}
/**
 * Get the entire translations list
 */
function getTranslationsList() {
  return translationsList;
}
/**
 * Get the keywords list of the given locale code
 */
function getKeywordsListOf(localeCode) {
  return translationsList[localeCode] || null;
}
/**
 * Set fallback locale code, if the keyword does not exist on current locale code,
 * then check it in the faLLBACK locale code instead
 */
function setFallbackLocaleCode(fallbackLocale) {
  const oldFallback = fallbackLocaleCode;
  fallbackLocaleCode = fallbackLocale;
  localizationEvents.triggerChange("fallback", fallbackLocale, oldFallback);
}
/**
 * Translate the given keyword in current locale code
 */
function trans(keyword, placeholders, converter = currentConverter) {
  return transFrom(currentLocaleCode, keyword, placeholders, converter);
}
/**
 * Translate using the default converter
 */
function plainTrans(keyword, placeholders) {
  return transFrom(currentLocaleCode, keyword, placeholders, plainConverter);
}
/**
 * Translate the given keyword for the given locale code
 * Please note this method accepts dot notation syntax
 */
function transFrom(
  localeCode,
  keyword,
  placeholders,
  converter = currentConverter,
) {
  let translation;
  if (typeof keyword === "object") {
    translation = keyword[localeCode] || keyword[fallbackLocaleCode];
  } else {
    translation =
      get(translationsList, `${localeCode}.${keyword}`) ||
      (fallbackLocaleCode
        ? get(translationsList, `${fallbackLocaleCode}.${keyword}`)
        : null);
  }
  if (!translation) return keyword;
  return placeholders ? converter(translation, placeholders) : translation;
}
/**
 * Get a translation object with automatic translation using object syntax
 * Please note this does not support nested objects, only keywords and their translations
 * i.e
 * const translations = transObject({
 *  name: {
 *     en: 'name',
 *    ar: 'الاسم'
 * }
 * });
 *
 * Usage: translations.name // returns the name in current locale code
 * If the keyword does not exist on current locale code, then it will check it in the fallback locale code
 *
 * If keyword is "p", then it will return a function that accepts keyword and its placeholders
 * If keyword is "plain", then the converter used in translation will be the plain converter
 */
function transObject(translations) {
  // use proxy
  return new Proxy(translations, {
    get(target, key) {
      if (key === "p") {
        return function (keyword, placeholders) {
          return transFrom(
            currentLocaleCode,
            target[keyword],
            placeholders,
            currentConverter,
          );
        };
      }
      if (key === "plain") {
        return function (keyword, placeholders) {
          return transFrom(
            currentLocaleCode,
            target[keyword],
            placeholders,
            plainConverter,
          );
        };
      }
      if (!target[key]) return transFrom(fallbackLocaleCode, key);
      return transFrom(currentLocaleCode, target[key]);
    },
  });
}
export {
  extend,
  getCurrentLocaleCode,
  getFallbackLocaleCode,
  getKeywordsListOf,
  getTranslationsList,
  groupedTranslations,
  plainTrans,
  setConverter,
  setCurrentLocaleCode,
  setFallbackLocaleCode,
  setTranslationsList,
  trans,
  transFrom,
  transObject,
};
